<template>
	<div>
		<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

		<q-collapsible
			class="shadow-2 q-pa-sm q-ma-xs rounded-border"
			icon="far fa-check-square"
			right-icon="far fa-check-square"
			:label="$t('how_to_create_the_game')"
		>
			<div class="q-ma-sm">
				<p>{{ $t('fill_in_the_blanks_instructions') }}</p>
				<p class="text-bold">{{ $t('example') }}</p>
				<p>{{ $t('fill_in_the_blanks_instructions_example') }}</p>
				<p class="q-mt-lg">
					<em
						><strong>{{ `${$t('warning')}: ` }}</strong
						>{{ $t('create_game_before_save') }}</em
					>
				</p>
			</div>
		</q-collapsible>

		<div class="col-xs-12 col-sm-12">
			<q-field :count="1024" class="col-5">
				<q-input
					debounce="1000"
					clearable
					clear-icon="close"
					filled
					color="primary"
					class="q-mt-lg"
					type="textarea"
					@input="checkGame"
					@clear="resetGame"
					v-model="game_data.game_text"
					:error="$v.game_data.game_text.$error"
					:placeholder="$t('add_the_game_sentences_here')"
				/>
			</q-field>

			<div v-if="game_data.game_options.length" class="col-xs-12 col-sm-12 q-mt-md">
				<p class="text-bold">{{ $t('words') }}</p>
				<span
					class="q-my-sm q-mr-sm"
					v-for="(opt, index) in game_data.game_options"
					:key="index"
				>
					<q-chip color="primary" text-color="white">{{ opt.text }}</q-chip>
				</span>
			</div>

			<div class="row justify-end q-mr-md q-mt-sm">
				<q-btn
					color="secondary"
					:label="$t('preview')"
					:disable="game_data.game_text && game_data.game_text.length < 10"
					@click="buildGamePreview"
				/>
			</div>

			<fill-in-the-blanks-preview
				v-if="show_game_preview"
				:show_game_preview="show_game_preview"
				@hide_preview="hidePreview()"
				:game_name="game_data.game_name"
				:game_words_arr="game_data.game_words_arr"
				:game_options="game_data.game_options"
				:word_slot="game_data.word_slot"
			/>
		</div>
	</div>
</template>

<script>
import FillInTheBlanksPreviewVue from 'components/admin/games/FillInTheBlanksPreview'
import { required, maxLength } from 'vuelidate/lib/validators'
import slugify from '../../../functions/slugify.js'

export default {
	name: 'AdminGameFillTheBlanks',

	props: ['can_save', 'saved_game_data', 'id_game'],

	data() {
		return {
			string_match_start: 0,
			string_match_end: 0,
			game_data: {
				game_text: '',
				game_name: 'fill_in_the_blanks',
				game_words_arr: [],
				game_options: [],
				word_slot: '____________',
			},
			show_game_preview: false,
			show_preview: false,
			game_built: false,
		}
	},

	components: {
		'fill-in-the-blanks-preview': FillInTheBlanksPreviewVue,
	},

	methods: {
		checkGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$v.game_data.game_text.$touch()
		},

		buildGamePreview() {
			if (this.game_data.game_options.length) {
				this.game_data.game_options = []
			}

			let newLines = /[\n]/g
			let wordRegex = /[((*)):,.?!]/g
			let fixed_words = this.game_data.game_text.replaceAll(newLines, ' \n ')

			this.game_data.game_words_arr = fixed_words.split(' ')

			this.game_data.game_words_arr = this.game_data.game_words_arr.forEach((el) => {
				if (el.startsWith('((')) {
					let fixedWord = el.replace(wordRegex, '')
					el = fixedWord
					this.game_data.game_options.push({
						text: el.trim(),
						slug: slugify(el.trim()),
						position: 0,
						value: '',
						correct: undefined,
					})
				}
			})

			if (
				this.game_data &&
				this.game_data.game_options &&
				!this.game_data.game_options.length
			) {
				this.$q.notify({
					message: this.$t('not_enough_special_words_to_create_game'),
					type: 'negative',
					icon: 'fas fa-ban',
				})
				return
			}

			this.game_data.game_options.forEach((opt) => {
				fixed_words = fixed_words.replace(`((${opt.text}))`, ' SPECIAL_WORD ')
			})

			this.game_data.game_words_arr = fixed_words.split(' ')

			let lastIndexPosSpecialWord = 0

			this.game_data.game_options.forEach((opt) => {
				let position = this.game_data.game_words_arr.findIndex(
					(e, index) => e === 'SPECIAL_WORD' && index >= lastIndexPosSpecialWord
				)

				lastIndexPosSpecialWord = position + 1
				opt.position = position
			})

			let biggestSpecialWordSize = Math.max(
				...this.game_data.game_options.map((e) => e.text.length)
			)
			let biggestSpecialWord = this.game_data.game_options.find(
				(e) => e.text.length === biggestSpecialWordSize
			)

			this.game_data.word_slot = biggestSpecialWord.text
				.split('')
				.map(() => '_')
				.join('')

			this.show_game_preview = true

			if (!this.$v.$error) {
				this.game_built = true
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		resetGame() {
			this.game_data.game_words_arr = []
			this.game_data.game_text = ''
			this.game_data.game_options = []
			this.string_match_start = 0
			this.string_match_end = 0
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
		} else {
			this.game_data = {
				game_text: '',
				game_name: 'fill_in_the_blanks',
				game_words_arr: [],
				game_options: [],
				word_slot: '____________',
			}
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				game_text: { required, maxLength: maxLength(1024) },
			},
		}
	},
}
</script>

<style lang="stylus" scoped>
.rounded-border
    border-radius 3px
</style>
